/**
 * EmailDiagnosticTile — surfaces GET /api/admin/email-delivery-diagnostic
 * inside the AdminTools page so directors / clientcare can see at a
 * glance whether outbound email is reaching clients & cashier.
 *
 * Read-only, additive. Errors flow through the canonical errorMessage(err, ...)
 * helper (utils/errorMessage.js) — no silent fallbacks.
 */
import { useState } from "react";
import {
  Mail, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { errorMessage } from "../utils/errorMessage";

function StatusLight({ ok, labelOk, labelBad }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold " +
        (ok
          ? "bg-emerald-900/30 text-emerald-400 border border-emerald-700/40"
          : "bg-red-900/30 text-red-400 border border-red-700/40")
      }
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {ok ? labelOk : labelBad}
    </span>
  );
}

function KvRow({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-xs py-1 border-b border-navy-border/40 last:border-b-0">
      <span className="text-txt-muted">{k}</span>
      <span className="text-txt font-mono">{String(v)}</span>
    </div>
  );
}

export default function EmailDiagnosticTile({ apiBase, getHeaders }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showFailures, setShowFailures] = useState(false);
  const [showLast10, setShowLast10] = useState(false);
  const [showDns, setShowDns] = useState(true);

  const fetchDiagnostic = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${apiBase}/api/admin/email-delivery-diagnostic`, {
        method: "GET",
        headers: getHeaders(),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        let detail = text;
        try {
          const j = JSON.parse(text);
          detail = j?.detail || text;
        } catch (_) { /* keep raw */ }
        throw new Error(`HTTP ${resp.status} — ${detail || resp.statusText}`);
      }
      const json = await resp.json();
      setData(json);
    } catch (err) {
      setError(errorMessage(err, "Failed to fetch email diagnostic"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const audit = data?.recent_send_audit || {};
  const statusCounts = audit.by_status_last_24h || {};
  const templateCounts = audit.by_template_last_24h || {};
  const failures = audit.last_5_failures || [];
  const last10 = audit.last_10_sends || [];
  const dns = data?.dns_check || null;

  return (
    <section className="glass-card p-6 rounded-md" data-testid="email-diagnostic-tile">
      <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-3 flex items-center gap-2">
        <Mail className="w-4 h-4" /> Email Delivery Diagnostic
      </h2>
      <p className="text-xs text-txt-muted mb-4">
        Read-only triage of the outbound-email gate and the last 24 hours of
        send-audit rows. Use this to confirm whether emails are reaching
        clients and the cashier.
      </p>

      <Button
        onClick={fetchDiagnostic}
        disabled={loading}
        data-testid="email-diagnostic-fetch"
        className="min-w-[220px] justify-start gap-2 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white transition-all disabled:opacity-40"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        {data ? "Refresh diagnostic" : "Run diagnostic"}
      </Button>

      {error && (
        <div
          className="mt-4 flex items-start gap-2 text-sm text-status-red bg-red-950/20 border border-red-800/40 rounded p-3"
          data-testid="email-diagnostic-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-4">
          {/* Headline traffic light */}
          <div className="flex flex-wrap items-center gap-3" data-testid="email-diagnostic-headline">
            <StatusLight
              ok={!!data.emails_enabled_effective}
              labelOk="EMAILS_ENABLED = true"
              labelBad="EMAILS_ENABLED is not true"
            />
            <StatusLight
              ok={!!data.graph_creds_set}
              labelOk="Graph creds set"
              labelBad="Graph creds missing"
            />
            <StatusLight
              ok={!!data.graph_notification_mailbox_set}
              labelOk="Graph mailbox set"
              labelBad="Graph mailbox missing"
            />
            <span className="text-[11px] text-txt-muted ml-auto">
              deploy: {data.deploy_tag || "unknown"}
            </span>
          </div>

          {/* Env field detail */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-navy-card/60 border border-navy-border rounded p-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-txt-muted mb-2">
                Environment
              </h3>
              <KvRow k="emails_enabled_env (raw)" v={data.emails_enabled_env ?? "(unset)"} />
              <KvRow k="emails_enabled_effective" v={String(data.emails_enabled_effective)} />
              <KvRow k="smtp_host_set (legacy)" v={String(data.smtp_host_set)} />
              <KvRow k="graph_creds_set" v={String(data.graph_creds_set)} />
              <KvRow k="graph_mailbox_set" v={String(data.graph_notification_mailbox_set)} />
              <KvRow k="generated_at" v={data.generated_at || ""} />
            </div>

            <div className="bg-navy-card/60 border border-navy-border rounded p-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-txt-muted mb-2">
                Last 24h — status
              </h3>
              {audit.total_last_24h ? (
                <>
                  <div className="text-xs text-txt-muted mb-2">
                    total rows: <span className="font-mono text-txt">{audit.total_last_24h}</span>
                  </div>
                  {Object.entries(statusCounts).map(([s, n]) => {
                    const total = audit.total_last_24h || 1;
                    const pct = Math.round((n / total) * 100);
                    const colour =
                      s === "sent" || s === "success" ? "bg-emerald-600"
                      : s === "disabled" ? "bg-amber-600"
                      : "bg-red-600";
                    return (
                      <div key={s} className="flex items-center gap-2 text-xs py-1">
                        <span className="w-20 truncate text-txt-muted">{s}</span>
                        <div className="flex-1 h-1.5 bg-navy-border rounded overflow-hidden">
                          <div className={`h-full ${colour}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-16 text-right font-mono text-txt">
                          {n} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </>
              ) : (
                <p className="text-xs text-txt-muted italic">
                  No email_audit rows in the last 24 hours.
                  {audit.audit_query_error
                    ? ` Query error: ${audit.audit_query_error}`
                    : ""}
                </p>
              )}
            </div>
          </div>

          {/* DNS forensics — BM-H7 part 3 */}
          {dns && (
            <div className="bg-navy-card/60 border border-navy-border rounded p-3" data-testid="email-diagnostic-dns">
              <button
                type="button"
                onClick={() => setShowDns((v) => !v)}
                className="w-full flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-txt-muted hover:text-txt"
              >
                {showDns ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Send-domain DNS ({dns.domain || "unknown"})
                <span className="ml-auto">
                  <StatusLight
                    ok={!!dns.overall_ok}
                    labelOk="all green"
                    labelBad="needs attention"
                  />
                </span>
              </button>
              {showDns && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-[110px_1fr_auto] items-start gap-2 text-xs">
                    <span className="text-txt-muted">MX</span>
                    <span className="font-mono text-txt break-all">
                      {dns.mx?.records?.length
                        ? dns.mx.records.join(", ")
                        : (dns.mx?.error || "no records")}
                    </span>
                    <StatusLight ok={!!dns.mx?.ok} labelOk="OK" labelBad="FAIL" />
                  </div>
                  <div className="grid grid-cols-[110px_1fr_auto] items-start gap-2 text-xs">
                    <span className="text-txt-muted">SPF</span>
                    <span className="font-mono text-txt break-all">
                      {dns.spf?.record || dns.spf?.error || "no record"}
                      {dns.spf?.record && (
                        <span className="block mt-0.5 text-[10px] text-txt-muted">
                          includes: outlook={String(!!dns.spf.includes_outlook)} · microsoft={String(!!dns.spf.includes_microsoft)}
                        </span>
                      )}
                    </span>
                    <StatusLight ok={!!dns.spf?.ok} labelOk="OK" labelBad="FAIL" />
                  </div>
                  {(dns.dkim || []).map((d) => (
                    <div key={d.selector} className="grid grid-cols-[110px_1fr_auto] items-start gap-2 text-xs">
                      <span className="text-txt-muted">DKIM {d.selector}</span>
                      <span className="font-mono text-txt break-all">
                        {d.records?.length ? d.records.join(", ") : (d.error || "no records")}
                        <span className="block mt-0.5 text-[10px] text-txt-muted">{d.fqdn}</span>
                      </span>
                      <StatusLight ok={!!d.ok} labelOk="OK" labelBad="FAIL" />
                    </div>
                  ))}
                  <div className="grid grid-cols-[110px_1fr_auto] items-start gap-2 text-xs">
                    <span className="text-txt-muted">DMARC</span>
                    <span className="font-mono text-txt break-all">
                      {dns.dmarc?.record || dns.dmarc?.error || "no record"}
                      {dns.dmarc?.policy && (
                        <span className="block mt-0.5 text-[10px] text-txt-muted">
                          policy: {dns.dmarc.policy}
                        </span>
                      )}
                    </span>
                    <StatusLight ok={!!dns.dmarc?.ok} labelOk="OK" labelBad="FAIL" />
                  </div>
                  {dns.error && (
                    <div className="text-xs text-status-red mt-1">DNS probe error: {dns.error}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Templates */}
          {Object.keys(templateCounts).length > 0 && (
            <div className="bg-navy-card/60 border border-navy-border rounded p-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-txt-muted mb-2">
                Last 24h — by template / event
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {Object.entries(templateCounts).map(([t, n]) => (
                  <KvRow key={t} k={t} v={n} />
                ))}
              </div>
            </div>
          )}

          {/* Last 10 sends — BM-H7 part 3, any status */}
          <div className="bg-navy-card/60 border border-navy-border rounded p-3" data-testid="email-diagnostic-last10">
            <button
              type="button"
              onClick={() => setShowLast10((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-txt-muted hover:text-txt"
            >
              {showLast10 ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Last 10 sends ({last10.length})
            </button>
            {showLast10 && (
              <div className="mt-3">
                {last10.length === 0 ? (
                  <p className="text-xs text-txt-muted italic">
                    No email_audit rows in the last 24 hours.
                  </p>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-txt-muted border-b border-navy-border">
                        <th className="text-left py-1 pr-2">when</th>
                        <th className="text-left py-1 pr-2">status</th>
                        <th className="text-left py-1 pr-2">template</th>
                        <th className="text-left py-1 pr-2">matter</th>
                        <th className="text-left py-1 pr-2">recipients</th>
                        <th className="text-left py-1">error</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {last10.map((f, i) => {
                        const st = String(f.status || "").toLowerCase();
                        const cls =
                          st === "sent" || st === "success"
                            ? "text-emerald-400"
                            : st === "disabled"
                            ? "text-amber-400"
                            : "text-red-400";
                        return (
                          <tr key={i} className="border-b border-navy-border/40 last:border-b-0 align-top">
                            <td className="py-1 pr-2 whitespace-nowrap">{f.timestamp || ""}</td>
                            <td className={`py-1 pr-2 ${cls}`}>{f.status || ""}</td>
                            <td className="py-1 pr-2">{f.template || ""}</td>
                            <td className="py-1 pr-2">{f.matter_reference_redacted || ""}</td>
                            <td className="py-1 pr-2">{(f.recipients_redacted || []).join(", ")}</td>
                            <td className="py-1 break-all max-w-xs">{f.error || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Failures (collapsed by default) */}
          <div className="bg-navy-card/60 border border-navy-border rounded p-3">
            <button
              type="button"
              onClick={() => setShowFailures((v) => !v)}
              data-testid="email-diagnostic-toggle-failures"
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-txt-muted hover:text-txt"
            >
              {showFailures ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Last 5 failures ({failures.length})
            </button>
            {showFailures && (
              <div className="mt-3">
                {failures.length === 0 ? (
                  <p className="text-xs text-txt-muted italic">
                    No non-success rows in the last 24 hours.
                  </p>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-txt-muted border-b border-navy-border">
                        <th className="text-left py-1 pr-2">when</th>
                        <th className="text-left py-1 pr-2">status</th>
                        <th className="text-left py-1 pr-2">template</th>
                        <th className="text-left py-1 pr-2">matter</th>
                        <th className="text-left py-1 pr-2">recipients</th>
                        <th className="text-left py-1">error</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {failures.map((f, i) => (
                        <tr key={i} className="border-b border-navy-border/40 last:border-b-0 align-top">
                          <td className="py-1 pr-2 whitespace-nowrap">{f.timestamp || ""}</td>
                          <td className="py-1 pr-2 text-red-400">{f.status || ""}</td>
                          <td className="py-1 pr-2">{f.template || ""}</td>
                          <td className="py-1 pr-2">{f.matter_reference_redacted || ""}</td>
                          <td className="py-1 pr-2">{(f.recipients_redacted || []).join(", ")}</td>
                          <td className="py-1 break-all max-w-xs">{f.error || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
